/**
 * The reporting window a delivery surface is allowed to NAME.
 *
 * A delivery card draws columns and then prints a period beneath its title.
 * Those two readings must come from one place. The moment the label resolves
 * its own window it starts describing a period the columns below it do not
 * draw — and a caption is believed, so the chart is read as if it covered the
 * caption's dates.
 *
 * That is exactly how Quantum's welcome card came to caption a chart of
 * Aug 10, Aug 11 and Aug 12 as "Aug 12–Aug 12": its label read the analysis
 * window of the AGGREGATE dataset, whose per-channel rows are all
 * synthetically keyed to the canonical window's END date, so both ends of the
 * range collapsed onto the last day while the chart drew the certified daily
 * series the label never saw.
 *
 * The rule, in order:
 *
 * 1. DATED EVIDENCE WINS. When a surface draws dated delivery columns, the
 *    window is the earliest and latest day IT DREW — nothing else. Not today,
 *    not the flight end, not the window that was requested. A flight running
 *    through Aug 23 that has reported through Aug 12 is reporting
 *    Aug 10–Aug 12, and a day nobody reported cannot widen the range because
 *    it is not in the chart either.
 * 2. Otherwise, the canonical window. A payload carrying no dated evidence
 *    draws window TOTALS, and those totals genuinely cover the whole canonical
 *    window — naming them by the single date their one bucket is keyed to
 *    would shrink a multi-day total to its last day, which is the same defect
 *    in miniature.
 * 3. Otherwise, a governed dataset's own resolved window — design fixtures and
 *    dataset-fed surfaces carry no canonical timeframe.
 * 4. Otherwise nothing. "No resolved window" is a truthful state. Today's date
 *    is not a substitute for a reporting day, at either end.
 *
 * Byte-identical in the Cortex client and the Quantum superadmin. The label
 * formats its own dates rather than taking a formatter: two portals printing
 * the same window two ways is the drift this module exists to end.
 *
 * The same module owns the BASIS half of that caption for the same reason —
 * see the reporting-basis section below.
 */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const NO_REPORTING_WINDOW_LABEL = "Current reporting window";

function toIsoDay(value) {
  const dateKey = String(value == null ? "" : value).slice(0, 10);
  return ISO_DAY.test(dateKey) ? dateKey : null;
}

/*
 * Windows arrive under three spellings across the reporting spine
 * (`start`/`startDate`/`dateStart`). Reading all three here is what keeps the
 * caller from having to know which shape it was handed.
 */
function readWindowBounds(window) {
  if (!window || typeof window !== "object") return null;
  const start = toIsoDay(window.start ?? window.startDate ?? window.dateStart);
  const end = toIsoDay(window.end ?? window.endDate ?? window.dateEnd);
  if (!end) return null;
  /* An inverted window is a broken window: keep the end it can prove and drop
     the start it cannot, rather than printing a range that runs backwards. */
  return { start: start && start <= end ? start : null, end };
}

/**
 * The observed reporting window, as `{ start, end, source }` — or null when
 * nothing on the payload can name one.
 *
 * `observedDates` is the dates of the dated delivery the surface ACTUALLY
 * DRAWS, not every date the payload carries. Pass the chart's own bucket
 * dates; pass null (or nothing) when the surface is drawing window totals.
 */
export function resolveObservedReportingWindow({
  observedDates = null,
  canonicalTimeframe = null,
  datasetWindow = null,
} = {}) {
  const dated = (Array.isArray(observedDates) ? observedDates : [])
    .map(toIsoDay)
    .filter(Boolean)
    .sort();
  if (dated.length) {
    return {
      start: dated[0],
      end: dated[dated.length - 1],
      source: "observed_delivery",
    };
  }

  const canonical = readWindowBounds(canonicalTimeframe);
  if (canonical) return { ...canonical, source: "canonical_window" };

  const dataset = readWindowBounds(datasetWindow);
  if (dataset) return { ...dataset, source: "dataset_window" };

  return null;
}

/*
 * Date-only keys, formatted as date-only keys: parsed at UTC midnight and
 * printed in UTC, so a calendar day never slides to its neighbour for a reader
 * west of Greenwich.
 */
function formatWindowDate(dateKey) {
  const parsed = new Date(`${dateKey}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

/**
 * The window as a reader sees it. A single reported day states that day at
 * both ends ("Aug 12–Aug 12") rather than collapsing to one date: the range is
 * the claim, and a one-day range is a real and useful thing to have said.
 */
export function formatObservedReportingWindowLabel(window) {
  const resolved = readWindowBounds(window);
  if (!resolved) return NO_REPORTING_WINDOW_LABEL;
  if (!resolved.start) return `Through ${formatWindowDate(resolved.end)}`;
  return `${formatWindowDate(resolved.start)}–${formatWindowDate(resolved.end)}`;
}

/*
 * ---------------------------------------------------------------------------
 * The reporting BASIS a delivery surface is allowed to name.
 * ---------------------------------------------------------------------------
 *
 * A delivery card's caption is one claim in two halves: WHAT the money is and
 * WHEN it was delivered. The window half already resolves here; the basis half
 * used to be written twice, and the two portals drifted exactly as this module
 * predicts. Cortex printed the client-facing sentence:
 *
 *   Client-billed reporting · Aug 10–Aug 12
 *
 * while Quantum prefixed the cross-platform CONTRACT's dual-basis label — the
 * string that exists to separate gross from net inside the operator analytics
 * experience — and captioned the identical payload:
 *
 *   Basis: Gross (client-billed) · Aug 10–Aug 12 · Freshness…
 *
 * Same advertiser, same campaign, same basis, same window, two readings. The
 * contract label is right where it is used (a plan series has to say which
 * basis it is planned on); it is wrong as a client-facing caption, which is
 * why the operator card read as the more technical of the two portals about a
 * number that is not more technical at all.
 *
 * So: the basis KEY is the governed input, and the display copy is derived
 * here for both portals. `gross` and `client_billed` are the same basis under
 * two spellings — gross IS the client-billed figure (fee/margin inclusive) —
 * and one caption should not make a reader translate between them. The copy
 * states the basis once: never `gross`, `basis` and `client-billed` stacked in
 * a single visible string.
 *
 * An unrecognised key is NOT quietly captioned as client-billed. Naming the
 * wrong basis over real money is worse than declining to name one, and a
 * caption is believed.
 */

export const CLIENT_BILLED_REPORTING_BASIS = "client_billed";
export const NET_MEDIA_REPORTING_BASIS = "net_media";
export const NO_REPORTING_BASIS_LABEL = "Reporting basis unavailable";

export const REPORTING_BASIS_LABELS = Object.freeze({
  [CLIENT_BILLED_REPORTING_BASIS]: "Client-billed reporting",
  [NET_MEDIA_REPORTING_BASIS]: "Net media reporting",
});

/* Every spelling the reporting spine actually carries, folded to one basis. */
const REPORTING_BASIS_ALIASES = new Map([
  ["gross", CLIENT_BILLED_REPORTING_BASIS],
  ["client_billed", CLIENT_BILLED_REPORTING_BASIS],
  ["clientbilled", CLIENT_BILLED_REPORTING_BASIS],
  ["net", NET_MEDIA_REPORTING_BASIS],
  ["net_media", NET_MEDIA_REPORTING_BASIS],
  ["netmedia", NET_MEDIA_REPORTING_BASIS],
]);

/**
 * The canonical basis key behind whatever spelling a payload used, or null
 * when nothing recognisable was supplied.
 */
export function resolveReportingBasis(spendBasis) {
  const key = String(spendBasis == null ? "" : spendBasis)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return REPORTING_BASIS_ALIASES.get(key) || null;
}

/**
 * The basis as a reader sees it — the primary caption on a delivery surface,
 * identical in both portals.
 */
export function formatReportingBasisLabel(spendBasis) {
  const basis = resolveReportingBasis(spendBasis);
  return basis ? REPORTING_BASIS_LABELS[basis] : NO_REPORTING_BASIS_LABEL;
}
